import { useCallback, useEffect, useMemo } from 'react';

import { useParams, useQuery } from '../../../contexts/RouterContext';
import { isValidReportFormat } from '../types';
import type { AnyReportFormatOption, ReportsV3Params, ReportsV3QueryParams, ReportType, ReportV3Format } from '../types';

/**
 * Giant map of the possible format options for each report type.
 * All of these are stored as SelectOptions, so they can be easily used in the dropdown.
 */
export const formatOptionsMap: Readonly<Record<ReportType, AnyReportFormatOption[]>> = {
  licensing: [
    { label: 'HTML', value: 'HTML' },
    { label: 'PDF', value: 'PDF' },
    { label: 'CSV', value: 'CSV' },
    { label: 'Markdown', value: 'MD' },
    { label: 'Plain Text', value: 'TXT' },
  ],
  sbom: [
    { label: 'SPDX 2.3', value: 'SPDX' },
    { label: 'SPDX 2.3 (JSON)', value: 'SPDX_JSON' },
    { label: 'SPDX 3.0 (JSON)', value: 'SPDX3_JSON' },
    { label: 'CycloneDX (JSON)', value: 'CYCLONEDX_JSON' },
    { label: 'CycloneDX (XML)', value: 'CYCLONEDX_XML' },
  ],
  vulnerability: [
    { label: 'PDF', value: 'PDF' },
  ],
  remediationGuidance: [
    { label: 'PDF', value: 'PDF' },
    { label: 'HTML', value: 'HTML' },
    { label: 'JSON', value: 'JSON' },
  ],
};

/**
 * The label the format selector shows for a format, e.g. "CycloneDX (JSON)" for CYCLONEDX_JSON.
 */
export const getFormatLabel = (reportType: ReportType, format: ReportV3Format): string =>
  formatOptionsMap[reportType]?.find((option) => option.value === format)?.label ?? format;

/**
 * Returns the first format option for the given report type.
 * @param reportType The report type to get the first format option for.
 * @returns The first format option for the given report type.
 */
const firstFormatForReportType = (reportType: ReportType): AnyReportFormatOption => {
  return (formatOptionsMap[reportType]?.[0] as AnyReportFormatOption); // Cast is safe here, we know these arrays are non-empty
};

/**
 * Hook for managing the format selector for a report.
 * It reads the URL query params for the format, and ensures that it is always set and is always valid for the report type.
 * If it's not, it sets the first valid format for the report type.
 * It returns the options and value for the format selector, and a function to set the format.
 */
export const useReportFormatSelector = () => {
  const { reportType } = useParams<ReportsV3Params>();
  const [query, setQuery] = useQuery<ReportsV3QueryParams>();

  const setFormat = useCallback((format: AnyReportFormatOption | null) => {
    if (format && isValidReportFormat(reportType, format.value)) {
      setQuery({ ...query, format: format.value });
    }
  }, [query, reportType, setQuery]);

  // Ensure that the format is always set and is always valid for the report type.
  // If it's not, set the first valid format for the report type.
  useEffect(() => {
    if (!query.format || !isValidReportFormat(reportType, query.format)) {
      setFormat(firstFormatForReportType(reportType));
    }
  }, [query, reportType, setFormat, setQuery]);

  const { options, value } = useMemo(() => {
    const optionsForReportType = formatOptionsMap[reportType];
    return {
      options: optionsForReportType,
      value: optionsForReportType
        .find((option) => option.value === query.format) ?? firstFormatForReportType(reportType),
    };
  }, [reportType, query.format]);

  return {
    options,
    value,
    setFormat,
  };
};
